/**
 * Flow Engine — internal contracts (MVP).
 * Bundle Builder is the first product surface; this module is not a public app module.
 */
import type { ExperienceCondition } from "@/lib/experience-definition/types";

export const FLOW_SCHEMA_VERSION = 1 as const;

/** Known presets — Bundle shipped; quote/booking/checkout planned. */
export type FlowPresetId = "bundle" | "quote" | "booking" | "checkout" | "custom";

export type FlowOutputType =
  | "order_draft"
  | "quote_draft"
  | "booking_draft"
  | "checkout_draft"
  | "custom";

export type FlowHandoff =
  | "cart"
  | "merchant_quote"
  | "booking"
  | "checkout"
  | "none"
  | "custom";


export type FlowLifecycleStatus = "draft" | "published" | "archived";

export type FlowStepType =
  | "product_picker"
  | "category_picker"
  | "collection_picker"
  | "manual_product"
  | "text_input"
  | "textarea"
  | "number"
  | "date"
  | "radio"
  | "checkbox"
  | "select"
  | "summary"
  | "review"
  | "message"
  | "container"
  | "divider"
  | "spacer";

export type FlowSelectionMode = "none" | "single" | "multiple";

export type FlowQtyMode = "none" | "min_max" | "exact";

/** Entity reference — never embed catalog rows in the definition. */
export interface EntityRef {
  domain: string;
  entityType: string;
  id: string;
}

export interface FlowSourceBinding {
  id: string;
  sourceKey:
    | "catalog.products"
    | "catalog.categories"
    | "catalog.collections"
    | "catalog.tags"
    | "catalog.manual"
    | "catalog.featured"
    | "catalog.search";
  stepId: string;
  query: Record<string, unknown>;
  lazy: boolean;
  required: boolean;
  selectionMode: FlowSelectionMode;
  minSelections?: number;
  maxSelections?: number;
}

export interface FlowStepQty {
  mode: FlowQtyMode;
  min?: number;
  max?: number;
  exact?: number;
}

export interface FlowStep {
  id: string;
  key: string;
  type: FlowStepType;
  label: string;
  description?: string;
  imageUrl?: string;
  order: number;
  enabled: boolean;
  required: boolean;
  selectionMode: FlowSelectionMode;
  qty: FlowStepQty;
  /** Optional UX grouping tag (not a domain container). */
  stageKey?: string;
  parentId?: string | null;
  /** Container constraints */
  container?: {
    minChildren?: number;
    maxChildren?: number;
    capacity?: number;
    allowedCategoryIds?: string[];
    allowedTagIds?: string[];
    maxQtyTotal?: number;
    minQtyTotal?: number;
  };
  sourceBindingIds: string[];
  helpText?: string;
  layout?: "list" | "grid" | "cards";
  /** Field config for input-like steps */
  field?: {
    placeholder?: string;
    options?: Array<{ value: string; label: string }>;
  };
  entryConditions?: FlowCondition;
  exitConditions?: FlowCondition;
}

export type FlowCondition =
  | ExperienceCondition
  | { type: "always" }
  | { type: "selection"; stepKey: string; op: "includes" | "empty" | "count_gte" | "count_lte"; value?: unknown }
  | { type: "variable"; key: string; op: "eq" | "neq" | "gt" | "gte" | "lt" | "lte"; value: unknown }
  | { type: "and"; conditions: FlowCondition[] }
  | { type: "or"; conditions: FlowCondition[] }
  | { type: "not"; condition: FlowCondition };

export type FlowRuleEffect =
  | { type: "show_step"; stepKey: string }
  | { type: "hide_step"; stepKey: string }
  | { type: "set_qty_max"; stepKey: string; max: number }
  | { type: "set_qty_min"; stepKey: string; min: number }
  | { type: "add_gift_ref"; productId: string; qty?: number }
  | { type: "discount_percent"; percent: number; priority?: number }
  | { type: "discount_fixed"; amount: number; priority?: number }
  | { type: "surcharge_percent"; percent: number; priority?: number }
  | { type: "surcharge_fixed"; amount: number; priority?: number }
  | { type: "set_variable"; key: string; value: unknown }
  | { type: "require_step"; stepKey: string };

export interface FlowRule {
  id: string;
  key: string;
  name: string;
  enabled: boolean;
  priority: number;
  exclusiveGroup?: string;
  when: FlowCondition;
  effects: FlowRuleEffect[];
}

export interface FlowOutputSpec {
  outputType: FlowOutputType;
  pricingMode: "checkout" | "display" | "none";
  handoff: FlowHandoff;
  /**
   * Precio fijo del pack (opcional). Si está set, el total del pack es este valor;
   * la diferencia vs la suma de catálogo se muestra como descuento (o recargo si suma < fijo).
   */
  fixedPrice?: number | null;
  /**
   * Si false, no se aplican descuentos/recargos de reglas (solo suma o precio fijo).
   * Default: true.
   */
  allowDiscounts?: boolean;
}

export interface FlowGraph {
  entryStepId: string;
  steps: FlowStep[];
  /** Linear next by order when transitions empty (MVP). */
  transitions: Array<{
    id: string;
    fromStepId: string;
    toStepId: string | "__complete__";
    condition?: FlowCondition;
    priority: number;
    isDefault: boolean;
  }>;
}

export interface FlowDefinition {
  schemaVersion: typeof FLOW_SCHEMA_VERSION;
  id: string;
  tenantId: string;
  slug: string;
  name: string;
  description?: string;
  presetId: FlowPresetId;
  lifecycleStatus: FlowLifecycleStatus;
  version: number;
  requiredCapabilities: string[];
  minPlanTier?: string;
  graph: FlowGraph;
  sourceBindings: FlowSourceBinding[];
  rules: FlowRule[];
  outputSpec: FlowOutputSpec;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  archivedAt?: string;
  imageUrl?: string;
  metadata?: Record<string, unknown>;
}

export interface FlowSelection {
  stepId: string;
  bindingId?: string;
  refs: EntityRef[];
  qtys?: Record<string, number>;
  captured?: Record<string, unknown>;
}

export interface FlowSession {
  id: string;
  flowId: string;
  snapshotVersion: number;
  channel: "preview" | "storefront" | "embed";
  status: "active" | "completed" | "abandoned";
  currentStepId: string;
  selections: FlowSelection[];
  variables: Record<string, unknown>;
  startedAt: string;
  updatedAt: string;
}

export interface PriceProjectionLine {
  ref: EntityRef;
  qty: number;
  unitPrice: number;
  lineTotal: number;
  label: string;
  gift?: boolean;
}

export interface PriceProjection {
  currency: string;
  subtotal: number;
  discountTotal: number;
  surchargeTotal: number;
  total: number;
  lines: PriceProjectionLine[];
  appliedRuleKeys: string[];
}

export interface FlowExecutionResult {
  outputType: FlowOutputType;
  draftHandoff: {
    lines: Array<{ productId: string; qty: number; unitPriceHint?: number; gift?: boolean; label?: string }>;
    message?: string;
    bundleId: string;
    bundleVersion: number;
    appliedRuleKeys: string[];
    priceProjection: PriceProjection;
  };
}
